import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  TablePagination,
  CircularProgress,
  Chip,
  Container,
  Stack,
  TextField,
  InputAdornment,
  alpha,
  Slide,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  capitalize,

} from '@mui/material';
import {
  ShoppingCart as OrderIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { offersService } from '../../services/api';
import { Offer, OffersResponse } from '../../types/order';
import { SelectChangeEvent } from '@mui/material';
import { useLocation } from 'react-router-dom';

const OrderManagement: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [ offers, setOffers ] = useState<Offer[]>( [] );
  const [ loading, setLoading ] = useState( true );
  const [ error, setError ] = useState<string | null>( null );
  const [ page, setPage ] = useState( 0 );
  const [ rowsPerPage, setRowsPerPage ] = useState( 10 );
  const [ totalCount, setTotalCount ] = useState( 0 );
  const [ searchTerm, setSearchTerm ] = useState( '' );

  const [ statusDialogOpen, setStatusDialogOpen ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ lokersId, setLockersId ] = useState( null )
  const [ offerId, setOffersId ] = useState<number>( 0 )

  const [ lockerToUpdate, setLockerToUpdate ] = useState<{ locker: Offer; newStatus: string } | null>( null );
  const location = useLocation();
  const initialFilter = location.state?.filter || 'all';

  const [ statusFilter, setStatusFilter ] = useState<string>( initialFilter );

  useEffect( () => {
    const fetchOffers = async () => {
      try {
        setLoading( true );
        const response: OffersResponse = await offersService.getOffers( {
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter,
          search: searchTerm.length >= 3 ? searchTerm : "",

        } );

        if ( response.success ) {
          setOffers( response.data );
          setTotalCount( response.pagination.total );
        } else {
          setError( response.message || 'Failed to fetch offers' );
          setToast( {
            open: true,
            message: response.message || 'Failed to fetch offers',
            severity: 'error'
          } );
        }
      } catch ( err ) {
        console.error( 'Error fetching offers:', err );
        setError( 'Failed to fetch offers' );
        setToast( {
          open: true,
          message: 'Failed to fetch offers',
          severity: 'error'
        } );
      } finally {
        setLoading( false );
      }
    };


    fetchOffers();
  }, [ page, rowsPerPage, statusFilter, lokersId, searchTerm ] );



  const handleAvailable = async () => {
    if ( lokersId ) {

      try {
        const response = await offersService.availableSet( lokersId, offerId );

        if ( response.success ) {
          setToast( {
            open: true,
            message: response.message,
            severity: 'success'
          } );
          setStatusDialogOpen( false );
          setLockersId( null )

        } else {
          setToast( {
            open: true,
            message: response.message || 'Failed to delete user',
            severity: 'error'
          } );
        }
      } catch ( error ) {
        console.error( 'Failed to delete user:', error );
        setToast( {
          open: true,
          message: 'Failed to delete user',
          severity: 'error'
        } );
      } finally {

      }
    }
  };




  const handleStatusClick = ( container: Offer, newStatus: string ) => {
    setLockersId( container.locker.id )
    setOffersId( container?.id )
    setStatusDialogOpen( true );
  };

  const handleDeleteLockerCancel = () => {
    setStatusDialogOpen( false );
  };

  const handleViewOrder = ( offerId: number ) => {
    navigate( `/orders/${ offerId }` );
  };

  const handleChangePage = ( event: unknown, newPage: number ) => {
    setPage( newPage );
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen( false );
  };

  const handleChangeRowsPerPage = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    setRowsPerPage( parseInt( event.target.value, 10 ) );
    setPage( 0 );
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };




  const handleStatusChange = ( event: SelectChangeEvent<string> ) => {
    const newStatus = event.target.value;
    setStatusFilter( newStatus );
    setPage( 0 ); // Reset to first page when changing status

  };


  const getStatusColor = ( status: string ) => {
    switch ( status ) {
      case 'COMPLETED':
      case 'completed':
      case 'BUYER_SATISFIED':
      case 'PAYMENT_COMPLETED':
        return 'success';
      case 'PENDING':
      case 'pending':
      case 'OFFER_CREATED':
      case 'OFFER_SENT':
      case 'OFFER_ACCEPTED':
      case 'SELLER_BOOKED_LOCKER':
      case 'SELLER_EXTENDED_LOCKER_TIME':
      case 'SELLER_PLACED_ITEM':
      case 'BUYER_PICKED_ITEM':
        return 'warning';
      case 'CANCELLED':
      case 'cancelled':
      case 'FAILED':
      case 'OFFER_DECLINED':
      case 'BUYER_UNSATISFIED':
      case 'BUYER_ORDER_CANCELLED':
      case 'SELLER_ORDER_CANCELLED':
      case 'OFFER_FAILED':
      case 'OFFER_FAILED_BY_SELLER':
        return 'error';
      case 'SUPPORT_PICKED_ITEM_FROM_LOCKER':
      case 'ITEM_PICKED_BY_SELLER_FROM_SUPPORT':
      case 'ITEM_PICKED_BY_SUPPORT_FROM_BUYER':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatStatus = ( status: string ) => {
    switch ( status ) {
      case 'OFFER_SENT':
        return 'Offer Sent';
      case 'OFFER_ACCEPTED':
        return 'Offer Accepted';
      case 'OFFER_DECLINED':
        return 'Offer Declined';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  // const filteredOffers = offers.filter( ( offer ) => {

  //   const matchesSearch =
  //     offer.title.toLowerCase().includes( searchTerm.toLowerCase() ) ||
  //     `${ offer.seller.firstName } ${ offer.seller.lastName }`.toLowerCase().includes( searchTerm.toLowerCase() ) ||
  //     `${ offer.buyer.firstName } ${ offer.buyer.lastName }`.toLowerCase().includes( searchTerm.toLowerCase() ) ||
  //     offer.id.toString().includes( searchTerm );

  //   return matchesSearch;
  // } );
  const filteredOffers = offers;  // backend already filtered



  if ( loading && offers.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading orders...
          </Typography>
        </Box>
      </Container>
    );
  }

  if ( error && offers.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Alert severity="error" sx={ { mb: 2 } }>
          { error }
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>

      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <OrderIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t.orders.title }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage and monitor all order transactions
                </Typography>
              </Box>
            </Stack>

            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } }>
              <Chip
                icon={ <ReceiptIcon /> }
                label={ `${ totalCount } orders` }
                color="primary"
                variant="outlined"
              />
            </Box>

            {/* Search Section */ }
            {/* <TextField
              fullWidth
              placeholder="Search by order ID, item title, seller, or buyer name..."
              value={ searchTerm }
              onChange={ ( e ) => setSearchTerm( e.target.value ) }
              InputProps={ {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              } }
              sx={ {
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              } }
            /> */}
          </Box>
          <Grid container spacing={ 2 } sx={ { mb: 3 } }>
            <Grid item xs={ 12 } md={ 6 }>
              <TextField
                fullWidth
                placeholder="Search by item title, seller, or buyer name..."
                value={ searchTerm }
                onChange={ ( e ) => {
                  setSearchTerm( e.target.value );
                  setPage( 0 );   // Reset page when search changes
                } }

                InputProps={ {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                } }
                sx={ {
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }
                } }
              />
            </Grid>
            <Grid item xs={ 12 } md={ 6 }>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={ statusFilter }
                  label={ t.common.status }
                  onChange={ handleStatusChange }
                >
                
                  <MenuItem value="all">{ "All" }</MenuItem>
                  <MenuItem value="OFFER_FAILED_LOCKER_OCCUPIED">{ "OFFER_FAILED_LOCKER_OCCUPIED" }</MenuItem>
                  <MenuItem value="OFFER_FAILED_BY_SELLER">{ "OFFER_FAILED_BY_SELLER" }</MenuItem>

                  <MenuItem value="ITEM_NOT_SUBMIT_TO_SUPPORT_TIME_EXPIRED">
                    { "ITEM_NOT_SUBMIT_TO_SUPPORT_TIME_EXPIRED" }
                  </MenuItem>

                  <MenuItem value="SUPPORT_PICKED_ITEM_FROM_LOCKER">
                    { "SUPPORT_PICKED_ITEM_FROM_LOCKER" }
                  </MenuItem>

                  <MenuItem value="BUYER_ORDER_CANCELLED">
                    { "BUYER_ORDER_CANCELLED" }
                  </MenuItem>

                  <MenuItem value="SELLER_SATISFIED">
                    { "SELLER_SATISFIED" }
                  </MenuItem>

                  <MenuItem value="OFFER_BOOKING_TIME_EXPIRED">
                    { "OFFER_BOOKING_TIME_EXPIRED" }
                  </MenuItem>

                  <MenuItem value="OFFER_ACCEPTANCE_TIME_EXPIRED">
                    { "OFFER_ACCEPTANCE_TIME_EXPIRED" }
                  </MenuItem>

                  <MenuItem value="ITEM_RETURN_TO_SELLER_AFTER_INVESTIGATION">
                    { "ITEM_RETURN_TO_SELLER_AFTER_INVESTIGATION" }
                  </MenuItem>

                  <MenuItem value="ITEM_RETURN_TO_BUYER_AFTER_INVESTIGATION">
                    { "ITEM_RETURN_TO_BUYER_AFTER_INVESTIGATION" }
                  </MenuItem>

                  <MenuItem value="OFFER_DECLINED">
                    { "OFFER_DECLINED" }
                  </MenuItem>

                  <MenuItem value="SELLER_PLACED_ITEM">{ "SELLER_PLACED_ITEM" }</MenuItem>
                  <MenuItem value="OFFER_SENT">{ "OFFER_SENT" }</MenuItem>
                  <MenuItem value="BUYER_UNSATISFIED">{ "BUYER_UNSATISFIED" }</MenuItem>
                  <MenuItem value="SELLER_PICKED_ITEM">{ "SELLER_PICKED_ITEM" }</MenuItem>
                  <MenuItem value="BUYER_ORDER_CANCELLED">{ "BUYER_ORDER_CANCELLED" }</MenuItem>
                  <MenuItem value="ITEM_PICKED_BY_SUPPORT_FROM_BUYER">{ "ITEM_PICKED_BY_SUPPORT_FROM_BUYER" }</MenuItem>
                  <MenuItem value="SELLER_BOOKED_LOCKER">{ "SELLER_BOOKED_LOCKER" }</MenuItem>
                  <MenuItem value="BUYER_SATISFIED">{ "BUYER_SATISFIED" }</MenuItem>
                  <MenuItem value="SELLER_BOOKED_LOCKER">{ "SELLER_BOOKED_LOCKER" }</MenuItem>
                  <MenuItem value="OFFER_FAILED_BY_SELLER">{ "OFFER_FAILED_BY_SELLER" }</MenuItem>
                  <MenuItem value="PAYMENT_COMPLETED">{ "PAYMENT_COMPLETED" }</MenuItem>
                  <MenuItem value="SELLER_ORDER_CANCELLED">{ "SELLER_ORDER_CANCELLED" }</MenuItem>
                  <MenuItem value="failed">{ "Failed Order" }</MenuItem>
                  <MenuItem value="successfull">{ "Successfull Order" }</MenuItem>
                  <MenuItem value="active">{ "Active Order" }</MenuItem>

                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Results Section */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            } }
          >

            { filteredOffers.length === 0 ? (

              <Box sx={ {
                textAlign: 'center',
                py: 8,
                color: 'text.secondary'
              } }>
                <OrderIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                <Typography variant="h6" sx={ { mb: 1 } }>
                  No orders found
                </Typography>
                <Typography variant="body2">
                  { searchTerm ? 'Try adjusting your search criteria' : 'No orders available' }
                </Typography>
              </Box>

            ) : ( <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={ {
                      backgroundColor: 'primary.main',
                      '& th': {
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }
                    } }>
                      <TableCell align="center">
                        ID
                      </TableCell>
                      {/* <TableCell align="left">
                      Item Title
                    </TableCell>
                    <TableCell align="center">
                      Price
                    </TableCell> */}
                      <TableCell align="left">
                        Title
                      </TableCell>
                      <TableCell align="left">
                        Seller
                      </TableCell>
                      <TableCell align="left">
                        Buyer
                      </TableCell>
                      <TableCell align="center">
                        Status
                      </TableCell>
                      <TableCell align="left">
                        Container LockerID
                      </TableCell>
                      <TableCell align="center">
                        Occupied
                      </TableCell>
                      <TableCell align="center">
                        Payment Status
                      </TableCell>
                      <TableCell align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { filteredOffers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={ 8 }>
                          <Box sx={ {
                            textAlign: 'center',
                            py: 8,
                            color: 'text.secondary'
                          } }>
                            <OrderIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                            <Typography variant="h6" sx={ { mb: 1 } }>
                              No orders found
                            </Typography>
                            <Typography variant="body2">
                              { searchTerm ? 'Try adjusting your search criteria' : 'No orders available' }
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOffers.map( ( offer ) => {

                        // --- Time logic ---
                        const now = new Date();
                        const bookedTime = new Date( offer?.bookedAt );
                        const expiryTime = new Date( bookedTime.getTime() + offer?.timeDuration * 60 * 1000 );
                        const isExpired = now >= expiryTime;

                        // --- Status + locker check ---
                        const isStatusValid =
                          ( offer?.status === "OFFER_FAILED" || offer?.status === "BUYER_ORDER_CANCELLED"
                          ) &&
                          offer?.id === offer?.locker?.offerId;

                        // --- Final combined logic ---
                        const showYes = isStatusValid && isExpired;

                        return (
                          <TableRow
                            key={ offer.id }
                            sx={ {
                              '&:hover': {
                                backgroundColor: ( theme ) => alpha( theme.palette.primary.main, 0.04 )
                              },
                              transition: 'background-color 0.2s ease'
                            } }
                          >
                            <TableCell align="center">
                              <Typography variant="subtitle2" >
                                { offer.id }
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="subtitle2">
                                { capitalize( offer.title ) }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="subtitle2" >
                                { offer.seller?.firstName } { offer.seller?.lastName }
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                { offer.seller?.email }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="subtitle2" >
                                { offer.buyer?.firstName } { offer.buyer?.lastName }
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                { offer.buyer.email }
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Chip
                                label={ formatStatus( offer?.status ) }
                                color={ getStatusColor( offer?.status ) as any }
                                size="small"
                                sx={ { fontWeight: 600 } }
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="subtitle2" >
                                { ( offer?.locker?.containerId && offer?.locker?.lockerId ) ? ( `${ offer?.locker?.containerId }-${ offer?.locker?.lockerId }` ) : "N/A" }
                              </Typography>
                            </TableCell>

                            {/* 🔥 HERE IS YOUR UPDATED SHOW-YES / SHOW-NO CHIP */ }
                            <TableCell align="center">
                              <Typography variant="subtitle2" fontWeight="600" color="primary.main">
                                <Chip
                                  label={ showYes ? t( 'Yes' ) : t( 'No' ) }
                                  color={ showYes ? 'success' : 'error' }
                                  onClick={ () => {
                                    if ( showYes ) {
                                      handleStatusClick( offer, "Occupied" );
                                    }
                                  } }
                                  size="small"
                                  variant="outlined"
                                  clickable={ showYes }
                                />
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              { offer.paymentStatus ? (
                                <Chip
                                  label={ offer.paymentStatus }
                                  color="primary"
                                  size="small"
                                  sx={ { fontWeight: 600 } }
                                />
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  N/A
                                </Typography>
                              ) }
                            </TableCell>

                            <TableCell align="center">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={ () => handleViewOrder( offer.id ) }
                                sx={ {
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 2,
                                  py: 0.5
                                } }
                              >
                                { t.orders.view }
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      } )

                    ) }
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={ [ 5, 10, 25 ] }
                component="div"
                count={ totalCount }
                rowsPerPage={ rowsPerPage }
                page={ page }
                onPageChange={ handleChangePage }
                onRowsPerPageChange={ handleChangeRowsPerPage }
              />


            </>
            ) }



          </Paper>
        </Box>
      </Slide>
      <Dialog
        open={ statusDialogOpen }
        onClose={ handleStatusCancel }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={ {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
          },
          '&:disabled': {
            background: 'grey.400',
            color: 'grey.600'
          },
          textTransform: 'none',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } } >
          { " Available Offer" }
        </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>

          <Typography>
            { `Are you sure you want to make this locker available ?` }
          </Typography>

        </DialogContent>
        <DialogActions>
          <Button onClick={ handleDeleteLockerCancel } disabled={ loading } variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }>
            Cancel
          </Button>
          <Button
            onClick={ handleAvailable }
            color={ "success" }
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? <CircularProgress size={ 24 } /> : "Available" }
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrderManagement; 